import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Breadcrumbs } from "components/navigation";
import { ShowHide } from "components/layout";
import { FormSelect, FormTextInput, FormLocationSelector } from "components/form-fields";
import { KeyBlock } from "components/key";
import { ListingTable } from "components/listing";
import { Modal } from "components/notifications";
import { CircleStatus, AssetStatus } from "components/status";
import { LinkButton, Button, TextButton } from "components/buttons";
import { Tooltip } from "components/tooltip";
import { Filter, FilterButton } from "components/filtering";
import { DateToLocal } from "components/dates";
import { Position } from "@blueprintjs/core";
import { useDebounce } from "hooks/useDebounce";
import { EarnedRecognitionService } from "services/EarnedRecognitionService";
import { AssetService } from "services/AssetService";
import { EmployeeService } from "services/EmployeeService";
import { UserService } from "services/UserService";
import { LinkService } from "services/LinkService";
import classNames from "classnames";
import moment from "moment";
import { isPeriodAtOrBeforeFloor, formatPeriodHeaderTooltip } from "./kpiPeriodWindow";
import { COUNT_KPI_KEY_TO_CODE } from "./kpiCodes";
import "./EarnedRecognition.css";

const defaultArray = [];
const emptyColours = [];

// API/anchor dates are exchanged in ISO date format.
const apiDateFormat = "YYYY-MM-DD";
// Infringement timestamps are shown to the day and minute in the detail modal.
const infringementDateFormat = "DD MMM YYYY HH:mm";
// Number of ISO weeks in a single ISO period (also the number of week columns in the listing).
const isoWeeksPerPeriod = 4;
// Legacy Vision's driver calendar frames the chosen date in week 3 of its fixed 4-week grid. Pointing
// the deep link at a day 17 days into the period (its third week) makes that calendar open aligned to
// the whole ISO period, with the period's first day as the first cell — mirroring the legacy KPI
// drilldown, which achieves the same by selecting (period end − 10 days).
const calendarWeekThreeOffsetDays = 17;
// The reporting calendar always has exactly 13 periods per year. In a 53-week ISO year (e.g. 2026)
// the 53rd week folds into period 13 (which then spans five weeks) — period number is capped at 13.
const isoPeriodsPerYear = 13;
// The listing defaults to the first page at 25 rows per page, ordered by Employee Name ascending.
const defaultPageSize = 25;
// The pagination contract takes a single letter: anything else silently sorts ascending.
const defaultSortDirection = "A";
// The three SortField values the Employee API allows. An unrecognised field is not rejected: the
// pagination contract substitutes the default, so the caret moves and the rows come back ordered by
// employee name with a 200. TotalInfringements is only allowed once 175507 has deployed to the
// Employee API, so until then sorting on it silently orders by name.
const employeeNameSortField = "EmployeeName";
const totalInfringementsSortField = "TotalInfringements";
const registrationSortField = "Registration";
const employeeNameHeader = "EMPLOYEE NAME";
const totalInfringementsHeader = "TOTAL INFRINGEMENTS";
const driverDaysHeader = "DRIVER DAYS";
const registrationHeader = "REGISTRATION";
// The summary strip above the listing reports the selected period only, so totals spanning the three
// assessed periods have to say so — otherwise the same words name two different measures on one
// screen and a driver appears to out-drive the whole period.
const threePeriodHeaderSuffix = " (3 PERIODS)";
// A repeat offender infringed in the selected period and each of the two before it, so D7 always
// lists exactly three period columns.
const isoPeriodsInRepeatOffenderTest = 3;
// D8 (Unaccounted KM) is a vehicle-scoped KPI: the page lists vehicles rather than drivers, and the
// summary shows total unaccounted km instead of infringements/driver days.
const unaccountedKmKpiCode = "D8";
// D7 (Repeat Offenders) qualifies a driver on infringements in the selected period AND in each of the
// two before it, so those three ISO periods are the evidence its listing shows where it has them.
const repeatOffendersKpiCode = "D7";
const selectedPeriodColumnClass = "er-drilldown-col-selected";
// Debounce window for the employee-name search input.
const searchDebounceTimeout = 750;
// Business areas the location selector should offer. Driver-hours data is employee-scoped, so the
// employee business areas are used here (matching the Risk employee listing).
const locationBusinessAreaIds = [1, 2];

// Derives the StartDate (first day, Monday) of a four-week ISO period from its YEAR and PERIOD
// NUMBER. Mirrors the period maths used by getIsoPeriodStart on the Driver KPI summary page, but
// starts from a period number rather than a date: period N begins on ISO week (N - 1) * 4 + 1.
function getIsoPeriodStartFromNumber(isoYear, isoPeriod) {
    const startWeek = (Number(isoPeriod) - 1) * isoWeeksPerPeriod + 1;
    return moment().isoWeekYear(Number(isoYear)).isoWeek(startWeek).startOf("isoWeek").format(apiDateFormat);
}

// Shifts an ISO year/period by whole periods (deltaPeriods may be negative). Works in period space
// (13 periods per ISO year) rather than week arithmetic, so it rolls over year boundaries correctly
// and never produces a period 14 from the 53rd week of a 53-week ISO year.
function shiftIsoPeriod(isoYear, isoPeriod, deltaPeriods) {
    const absolutePeriod = Number(isoYear) * isoPeriodsPerYear + (Number(isoPeriod) - 1) + deltaPeriods;
    return {
        isoYear: Math.floor(absolutePeriod / isoPeriodsPerYear),
        isoPeriod: (absolutePeriod % isoPeriodsPerYear) + 1
    };
}

// The three periods the repeat offender rule assesses for a selected period: that period and the two
// before it, oldest first, in the shape the API returns them on a D7 row.
function buildRepeatOffenderPeriodColumns(isoYear, isoPeriod) {
    const columns = [];
    for (let index = isoPeriodsInRepeatOffenderTest - 1; index >= 0; index--) {
        columns.push({ ...shiftIsoPeriod(isoYear, isoPeriod, -index), isSelectedPeriod: index === 0 });
    }
    return columns;
}

// The ISO year/period containing today, used to detect (and reset to) the current period. Period is
// capped at 13: week 53 of a 53-week ISO year folds into period 13, not a (non-existent) period 14.
function getCurrentIsoPeriod() {
    return {
        isoYear: moment().isoWeekYear(),
        isoPeriod: Math.min(isoPeriodsPerYear, Math.ceil(moment().isoWeek() / isoWeeksPerPeriod))
    };
}

// Sums a numeric field across an employee's weeks, treating missing values as zero.
function sumWeeks(weeks, field) {
    if (weeks == null) {
        return 0;
    }

    return weeks.reduce((total, week) => total + (week[field] || 0), 0);
}

// Driver KPI Drilldown page (PBI 160899). Reached by clicking a KeyBlock on the Driver KPI summary;
// the selected ISO year/period and KPI arrive as query-string params. The KPI query param is the
// single source of truth for the selected KPI — the dropdown selection drives navigation, which
// re-renders the page from the new param rather than holding the selection in separate state.
export function DriverKpiDrilldown({ breadcrumbs = defaultArray }) {
    const [searchParams] = useSearchParams();
    const isoYear = searchParams.get("isoYear");
    const isoPeriod = searchParams.get("isoPeriod");
    const kpi = searchParams.get("kpi");
    const navigate = useNavigate();

    // D8 switches the page into vehicle mode: the listing shows vehicles, the driver-only filters and
    // summary tiles are swapped out, and the search/sort target Registration rather than Employee Name.
    const isUnaccountedKm = kpi === unaccountedKmKpiCode;

    // The count-type KPIs (D7 Repeat Offenders, D8 Unaccounted KM, D9 MSI) have no trigger band, so
    // their Period Status tile shows the failures count (e.g. "1 infringement") rather than a
    // coloured KeyBlock.
    const isCountKpi = Object.values(COUNT_KPI_KEY_TO_CODE).includes(kpi);

    // D7 is the only KPI whose rows can carry period columns in place of week columns (see
    // usePeriodColumns).
    const isRepeatOffenders = kpi === repeatOffendersKpiCode;

    const [kpiOptions, setKpiOptions] = useState(defaultArray);
    const [loading, setLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    // The earliest date the account has KPI data for (when it started sending to DVSA or the calc was
    // turned on), returned by the KPI dashboard endpoint. Bounds how far back the period stepper pages.
    const [kpiStartFloor, setKpiStartFloor] = useState(null);

    // Trigger-threshold bands (per-KPI severity description + pass-rate range) used to name the
    // Period Status severity in the KeyBlock tooltip (percentage KPIs only).
    const [driverHoursThresholds, setDriverHoursThresholds] = useState(defaultArray);

    const [period, setPeriod] = useState(null);
    const [periodLoading, setPeriodLoading] = useState(true);
    const [periodHasError, setPeriodHasError] = useState(false);

    // Filter option lists for the Driver Type / Driver Status dropdowns.
    const [driverTypeOptions, setDriverTypeOptions] = useState(defaultArray);
    const [driverStatusOptions, setDriverStatusOptions] = useState(defaultArray);

    // Status / ownership attribute mappings used to colour and icon the AssetStatus block beside
    // each D8 vehicle registration (matching the Maintenance drilldown's registration cell).
    const [assetStatusMappings, setAssetStatusMappings] = useState(defaultArray);
    const [assetOwnershipMappings, setAssetOwnershipMappings] = useState(defaultArray);

    // Listing state — mirrors the canonical RiskListing pattern.
    const [employees, setEmployees] = useState(defaultArray);
    // Whether the Employee API returns `periods` on D7 rows; null until a response with rows says.
    // Decides the empty-listing placeholders, because the API deploys separately from this page.
    const [listingCarriesPeriods, setListingCarriesPeriods] = useState(null);
    // Vehicle listing rows, populated only when D8 (Unaccounted KM) is selected.
    const [vehicles, setVehicles] = useState(defaultArray);
    const [totalRecords, setTotalRecords] = useState(0);
    const [listingLoading, setListingLoading] = useState(true);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [searchTerm, setSearchTerm] = useState("");
    const [tableSortDir, setTableSortDir] = useState(defaultSortDirection);
    // The column the user last sorted by, held as the column's stable key rather than its rendered
    // header text so a header whose wording varies (D7's three-period totals) keeps its sort.
    const [sortedByColumnKey, setSortedByColumnKey] = useState(null);

    // Driver Type / Driver Status filters now sit behind the filter button + panel (mirroring the
    // Collisions listing). The panel reports its selections through onFilterChange into appliedFilters.
    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(defaultArray);

    // Location selector state — kept local rather than persisted to Redux, as the selection is
    // scoped to this page.
    const [customLocations, setCustomLocations] = useState(null);
    const [selectedLocations, setSelectedLocations] = useState(defaultArray);
    const [useHierarchy, setUseHierarchy] = useState(true);
    // Tracks whether the asynchronous location load has resolved (success or failure). The listing
    // fetch is gated on this so it fires exactly once, after locations are known — preventing the
    // fresh-mount double-fire (one fetch before locations load, one after) whose concurrent second
    // call intermittently 500s and clobbers the good first response.
    const [locationsLoaded, setLocationsLoaded] = useState(false);

    // The employee whose infringements modal is open. Local state, mirroring ErAlerts' infoAlert.
    const [infringementsEmployee, setInfringementsEmployee] = useState(null);

    // The ISO week the infringements modal is filtered to; null shows all weeks (the period total).
    const [selectedWeekNumber, setSelectedWeekNumber] = useState(null);

    const debouncedSearchTerm = useDebounce(searchTerm, searchDebounceTimeout);

    // Refs guarding against duplicate fetches when several dependencies settle in the same render.
    const currentPageNumber = useRef();
    const currentPageSize = useRef();
    const currentSearchTerm = useRef();
    const currentTableSortDir = useRef();
    const currentAppliedFilters = useRef();
    const currentLocations = useRef();
    const currentStartDate = useRef();
    const currentKpi = useRef();
    const currentSortField = useRef();

    // Monotonic id of the most recently issued listing request. A response is applied only if its id
    // still matches the latest issued id, so a slow earlier response can never overwrite a newer one.
    const requestSequence = useRef(0);

    // The StartDate is derived from the route year/period so the API resolves the same ISO period the
    // user drilled into. It anchors both the listing search and the calendar deep link.
    const startDate = getIsoPeriodStartFromNumber(isoYear, isoPeriod);

    // The ISO period containing today. The next/reset stepper controls are disabled when the route is
    // already on this period (no future periods to step to, nothing to reset to).
    const currentPeriod = getCurrentIsoPeriod();
    const isCurrentPeriod = Number(isoYear) === currentPeriod.isoYear && Number(isoPeriod) === currentPeriod.isoPeriod;

    // The earliest period the account has KPI data for. There is nothing to drill into below it, so the
    // Previous stepper is bounded here. A null/absent floor imposes no lower bound.
    const isAtOrBeforeFloor = isPeriodAtOrBeforeFloor(Number(isoYear), Number(isoPeriod), kpiStartFloor);

    // Serialised key of the applied filters (name:value pairs). Keying the effects and the fetch guard
    // on this rather than appliedFilters.length ensures changing a single-select filter's value (same
    // count, different value) still triggers a refetch.
    const appliedFiltersKey = appliedFilters.map((filter) => `${filter.name}:${filter.value}`).join("|");

    // D7's three assessed ISO periods, oldest first. Taken from the first row because every row carries
    // the same three; while there are no rows they are derived from the route so the columns do not
    // shift when the rows land - unless the API has only ever returned weeks, in which case the week
    // placeholders are what the rows will match.
    let periodColumns = defaultArray;
    if (isRepeatOffenders) {
        if (employees.length > 0) {
            if (employees[0].periods != null) {
                periodColumns = employees[0].periods;
            }
        } else if (listingCarriesPeriods !== false) {
            periodColumns = buildRepeatOffenderPeriodColumns(isoYear, isoPeriod);
        }
    }

    // The single decision behind the period columns — the headers, the row cells, the driver-days
    // source and the totals' wording all read this, so they cannot describe the listing differently.
    // The Employee API deploys separately, so until it returns `periods` the page falls back to the
    // week columns rather than heading columns it has no cells for.
    const usePeriodColumns = periodColumns.length > 0;

    // The week fallback totals the selected period only, so it keeps the plain headers.
    const totalsHeaderSuffix = usePeriodColumns ? threePeriodHeaderSuffix : "";
    const renderedTotalInfringementsHeader = `${totalInfringementsHeader}${totalsHeaderSuffix}`;
    const renderedDriverDaysHeader = `${driverDaysHeader}${totalsHeaderSuffix}`;

    // The sortable headers, the sorted-column state and the posted SortField all derive from this one
    // list, so a header's wording cannot drift away from the field it sorts on. First entry is the
    // listing's default sorted column; the D8 vehicle table has no Total Infringements column at all.
    // Only D7 offers a sortable row total: its total spans the three assessed periods, so ordering by
    // it is part of reading that listing. Every other driver KPI totals the selected period alone and
    // sorts by name only.
    const driverSortableColumns = [
        { key: employeeNameHeader, header: employeeNameHeader, sortField: employeeNameSortField }
    ];

    if (isRepeatOffenders) {
        driverSortableColumns.push({ key: totalInfringementsHeader, header: renderedTotalInfringementsHeader, sortField: totalInfringementsSortField });
    }

    const sortableColumns = isUnaccountedKm
        ? [
            { key: registrationHeader, header: registrationHeader, sortField: registrationSortField }
        ]
        : driverSortableColumns;
    // Switching KPI can leave the sorted column off the new listing, so a column the current listing
    // does not offer falls back to that listing's default rather than sorting on a missing field.
    const activeSortColumn = sortableColumns.find((column) => column.key === sortedByColumnKey) ?? sortableColumns[0];
    const sortableHeaders = sortableColumns.map((column) => column.header);
    const sortField = activeSortColumn.sortField;

    useEffect(() => {
        // The KPI dropdown labels and the Period Status colour/percentage come from the Driver Hours
        // KPI Dashboard endpoint (#160871) — the same source the summary page uses. The drilled period
        // is the most-recent period in the returned window, located below by ISO year/period.
        setLoading(true);
        setHasError(false);
        EarnedRecognitionService.getDriverHoursKpis(startDate).then(
            (response) => {
                // The dashboard returns the percentage KPIs (D1-D6) under `kpis` and the count KPIs
                // under `counts`, keyed by name rather than D-code. Merge them into a single option list
                // so the dropdown offers all nine KPIs: the D1-D6 entries pass through unchanged, and each
                // count is remapped to its D-code via COUNT_KPI_KEY_TO_CODE and reshaped to the kpi option
                // shape. Counts carry no per-period values, so `values` is null (the Period Status colour
                // then falls back to an uncoloured block). Counts whose key is not in the map are skipped.
                const countOptions = (response.counts || [])
                    .filter((count) => COUNT_KPI_KEY_TO_CODE[count.key] != null)
                    .map((count) => {
                        return { key: COUNT_KPI_KEY_TO_CODE[count.key], label: count.label, values: null };
                    });
                setKpiOptions([...response.kpis, ...countOptions]);
                // The floor is account-level and stable, so it is only set on success and never reset to
                // null at fetch start — that keeps the Previous button from flickering enabled while the
                // user steps between periods.
                setKpiStartFloor(response.kpiStartFloor ?? null);
                setLoading(false);
            },
            () => {
                setHasError(true);
                setLoading(false);
            }
        );
    }, [startDate]);

    useEffect(() => {
        // The per-band trigger thresholds (severity description + pass-rate range per KPI) name the
        // Period Status severity in the KeyBlock tooltip. Fetched once from the shared thresholds
        // endpoint; failure is non-fatal — the tooltip is simply not shown.
        EarnedRecognitionService.getKpiThresholds().then(
            (response) => {
                setDriverHoursThresholds(response.driverHoursThresholds ?? defaultArray);
            },
            () => {
                setDriverHoursThresholds(defaultArray);
            }
        );
    }, []);

    useEffect(() => {
        // Load the Driver Type / Driver Status filter options once from the shared employee options
        // endpoint (the same source the employee listing pages use). Failure here is non-fatal: the
        // dropdowns simply offer no values, and the listing still loads.
        EmployeeService.getEmployeeOptions().then(
            (response) => {
                setDriverTypeOptions(response.employeeTypes);
                setDriverStatusOptions(response.employeeStatuses);
            },
            () => {
                setDriverTypeOptions(defaultArray);
                setDriverStatusOptions(defaultArray);
            }
        );
    }, []);

    useEffect(() => {
        // Load the AssetStatus colour/icon mappings from the shared asset options endpoint (the same
        // source the Maintenance drilldown uses). Only the D8 vehicle listing renders the AssetStatus
        // block, so the fetch is skipped for the driver KPIs; selecting D8 later re-runs this effect
        // (the KPI arrives via the query string, so the page re-renders rather than remounting).
        // Failure here is non-fatal: the block simply renders uncoloured with no icon or tooltip.
        if (!isUnaccountedKm) {
            return;
        }
        AssetService.getAssetOptions(true).then(
            (response) => {
                setAssetStatusMappings(response.assetStatuses.map((status) => {
                    return { name: status.name, colours: status.colour, id: status.id };
                }));
                setAssetOwnershipMappings(response.assetOwnerships.map((ownership) => {
                    return { name: ownership.name, colours: ownership.colour, id: ownership.id, className: ownership.style, icon: ownership.icon };
                }));
            },
            () => {
                setAssetStatusMappings(defaultArray);
                setAssetOwnershipMappings(defaultArray);
            }
        );
    }, [isUnaccountedKm]);

    useEffect(() => {
        // Load the locations (with hierarchy) for the employee business areas, merged across them, and
        // default the selection to every location — mirroring the Collisions listing. getLoggedInUser-
        // LocationsForMultipleBusinessAreas returns both the flat list and the hierarchy, so the selector
        // can show the hierarchical view; the older getFormLocationSelectorCustomLocations dropped it.
        UserService.getLoggedInUserLocationsForMultipleBusinessAreas(locationBusinessAreaIds, true, true).then(
            (response) => {
                setCustomLocations(response);
                setSelectedLocations(response.location[0].items.map((item) => item.id));
                setLocationsLoaded(true);
            },
            () => {
                setCustomLocations(null);
                setLocationsLoaded(true);
            }
        );
    }, []);

    useEffect(() => {
        // Reset to the first page whenever the period, KPI, search term or any filter changes, so the
        // user is not left on a now-out-of-range page. This runs before the fetch effect below.
        setPageNumber(1);
    }, [startDate, kpi, debouncedSearchTerm, appliedFiltersKey, selectedLocations]);

    useEffect(() => {
        // Wait until the locations have resolved before the first fetch, so the listing fires once with
        // the resolved location filters rather than once before and once after the async load.
        if (!locationsLoaded) {
            return;
        }
        fetchListing();
    }, [isoYear, isoPeriod, kpi, pageNumber, pageSize, sortField, tableSortDir, debouncedSearchTerm, appliedFiltersKey, selectedLocations, locationsLoaded]);

    function fetchListing() {
        // Guard against re-running an identical fetch (e.g. when the page-reset effect and a filter
        // change land in the same render). Location selection is compared by its serialised contents.
        const selectedLocationsKey = selectedLocations.join(",");
        if (currentPageNumber.current === pageNumber
            && currentPageSize.current === pageSize
            && currentSearchTerm.current === debouncedSearchTerm
            && currentTableSortDir.current === tableSortDir
            && currentSortField.current === sortField
            && currentAppliedFilters.current === appliedFiltersKey
            && currentLocations.current === selectedLocationsKey
            && currentStartDate.current === startDate
            && currentKpi.current === kpi) {
            return;
        }

        currentPageNumber.current = pageNumber;
        currentPageSize.current = pageSize;
        currentSearchTerm.current = debouncedSearchTerm;
        currentTableSortDir.current = tableSortDir;
        currentSortField.current = sortField;
        currentAppliedFilters.current = appliedFiltersKey;
        currentLocations.current = selectedLocationsKey;
        currentStartDate.current = startDate;
        currentKpi.current = kpi;

        // Claim the next request id; only the response carrying this id is allowed to update state.
        const requestId = requestSequence.current + 1;
        requestSequence.current = requestId;

        setListingLoading(true);
        setPeriodLoading(true);
        setPeriodHasError(false);

        // PaginationSearchRequest shape: page size is `requestCount`, and `filters` is a list of
        // { key, value } string pairs (single string value), not { field, values: [] }. Kpi is
        // required (the backend search is KPI-scoped). The applied filters carry their backend key in
        // `filter.name` ("DriverTypeId" / "DriverStatusId").
        const filters = [
            { key: "Kpi", value: kpi },
            { key: "StartDate", value: startDate }
        ];
        // The Driver Type / Driver Status filters only apply to drivers, so they are omitted for the
        // D8 vehicle listing.
        if (!isUnaccountedKm) {
            appliedFilters.forEach((filter) => {
                filters.push({ key: filter.name, value: `${filter.value}` });
            });
        }
        selectedLocations.forEach((locationId) => {
            filters.push({ key: "Location", value: `${locationId}` });
        });

        const request = {
            searchTerm: debouncedSearchTerm,
            pageNumber: pageNumber,
            requestCount: pageSize,
            sortField: sortField,
            sortDirection: tableSortDir,
            filters: filters
        };

        EarnedRecognitionService.searchDriverHoursEmployees(request).then(
            (response) => {
                // Ignore a response that has been superseded by a later request.
                if (requestId !== requestSequence.current) {
                    return;
                }

                setPeriod(response.period);
                // D8 returns its rows under `vehicles` (and an empty `employees`); every driver KPI
                // returns rows under `employees`. Populate the active listing and clear the other.
                if (isUnaccountedKm) {
                    setVehicles(response.vehicles.data);
                    setTotalRecords(response.vehicles.totalCount);
                    setEmployees(defaultArray);
                } else {
                    setEmployees(response.employees.data);
                    setTotalRecords(response.employees.totalCount);
                    setVehicles(defaultArray);
                    // An empty page says nothing about the API's shape, so only rows update this. Only D7
                    // rows ever carry periods, so only a D7 response may decide it; another KPI's shape
                    // says nothing about D7's.
                    if (isRepeatOffenders && response.employees.data.length > 0) {
                        setListingCarriesPeriods(response.employees.data[0].periods != null);
                    }
                }
                setPeriodLoading(false);
                setListingLoading(false);
            },
            () => {
                // Ignore a response that has been superseded by a later request.
                if (requestId !== requestSequence.current) {
                    return;
                }

                setPeriodHasError(true);
                setPeriodLoading(false);
                setListingLoading(false);
            }
        );
    }

    // Map the KPI options to the FormSelect item shape ({ id, name }).
    const selectItems = kpiOptions.map((option) => {
        return { id: option.key, name: option.label };
    });

    // The Driver Type / Driver Status filters are single-select because the backend reads a single
    // value per filter key. driverTypeOptions / driverStatusOptions are already { id, name }.
    const pageFilters = [
        { displayName: "Driver Type", name: "DriverTypeId", singleSelect: true, items: driverTypeOptions },
        { displayName: "Driver Status", name: "DriverStatusId", singleSelect: true, items: driverStatusOptions }
    ];

    // Resolve the heading to the selected KPI's label, falling back to the raw key from the route
    // until the options have loaded.
    const selectedKpi = kpiOptions.find((option) => option.key === kpi);
    const headingText = selectedKpi != null ? selectedKpi.label : kpi;

    // The selected KPI's value for the drilled period (from #160871) supplies the Period Status
    // colour; 161109 does not return a colour. For the count KPIs (D7-D9) the merged option has no
    // values, so this resolves to null and the colour falls back to an uncoloured block.
    const selectedKpiValue = selectedKpi != null && selectedKpi.values != null
        ? selectedKpi.values.find((value) => value.isoYear === Number(isoYear) && value.isoPeriod === Number(isoPeriod))
        : null;

    // The Period Status VALUE comes from the search response's period.kpis (#161109), which carries all
    // nine D-codes for the drilled period. Percentage KPIs (D1-D6) render as a percentage; count KPIs
    // (D7-D9) have a null percentage and render their failures count with an infringement unit. No match / period not loaded
    // renders an empty string.
    function resolvePeriodStatusValue() {
        if (period == null || period.kpis == null) {
            return "";
        }

        const periodKpi = period.kpis.find((periodKpiEntry) => periodKpiEntry.kpi === kpi);
        if (periodKpi == null) {
            return "";
        }

        if (periodKpi.percentage != null) {
            return `${periodKpi.percentage}%`;
        }

        // Count KPIs render their failures count with the infringement unit (e.g. "1 infringement",
        // "3 infringements") so the bare number reads clearly in the Period Status tile.
        let infringementUnit = "infringements";
        if (periodKpi.failures === 1) {
            infringementUnit = "infringement";
        }
        return `${periodKpi.failures} ${infringementUnit}`;
    }

    // Resolves the Period Status KeyBlock colours from the selected KPI's trigger colour for the
    // drilled period (#160871). A KPI that did not trigger has no colour, so the block is left empty.
    function resolvePeriodStatusColours() {
        if (period == null || selectedKpiValue == null || selectedKpiValue.triggerColourHex == null) {
            return emptyColours;
        }

        // An open period renders the colour as a bottom-left triangle (KeyBlock draws the fill via its
        // four border colours: top, right, bottom, left); a closed period fills the whole block.
        if (period.isOpen) {
            return ["#FFFFFF", "#FFFFFF", selectedKpiValue.triggerColourHex, selectedKpiValue.triggerColourHex];
        }

        return selectedKpiValue.triggerColourHex;
    }

    // Resolves the Period Status tooltip text: the selected KPI's trigger-threshold band description
    // for the drilled period, with an open-period note appended. Applies to the percentage KPIs that
    // render a KeyBlock; the count KPIs have no band so this returns an empty string (no tooltip). The
    // band is matched by KPI code and the period's pass percentage falling within the band's range.
    function resolvePeriodStatusTooltip() {
        if (period == null || selectedKpiValue == null || selectedKpiValue.percentage == null) {
            return "";
        }

        const band = driverHoursThresholds.find((threshold) =>
            threshold.kpiCode === kpi
            && selectedKpiValue.percentage >= threshold.minPercentage
            && selectedKpiValue.percentage <= threshold.maxPercentage);

        if (band == null) {
            return "";
        }

        return period.isOpen ? `${band.description} - period open` : band.description;
    }

    function handleSelectKpi(selectedItem) {
        navigate(`/earned-recognition/driver-kpi?isoYear=${isoYear}&isoPeriod=${isoPeriod}&kpi=${selectedItem.id}`);
    }

    // The ISO period stepper changes the selected period while keeping the current KPI. Each control
    // resolves a target year/period and navigates with the shared driver-kpi URL shape.
    function navigateToPeriod(targetYear, targetPeriod) {
        navigate(`/earned-recognition/driver-kpi?isoYear=${targetYear}&isoPeriod=${targetPeriod}&kpi=${kpi}`);
    }

    function onPreviousPeriod() {
        if (isAtOrBeforeFloor) {
            return;
        }
        const target = shiftIsoPeriod(isoYear, isoPeriod, -1);
        navigateToPeriod(target.isoYear, target.isoPeriod);
    }

    function onNextPeriod() {
        const target = shiftIsoPeriod(isoYear, isoPeriod, 1);
        navigateToPeriod(target.isoYear, target.isoPeriod);
    }

    function onResetPeriod() {
        navigateToPeriod(currentPeriod.isoYear, currentPeriod.isoPeriod);
    }

    function onSearchChange(event) {
        setSearchTerm(event.target.value);
    }

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
    }

    function onTableSort(header, direction) {
        const column = sortableColumns.find((candidate) => candidate.header === header);
        setSortedByColumnKey(column != null ? column.key : null);
        setTableSortDir(direction);
    }

    function onToggleFilter() {
        setShowFilters((previous) => {
            return !previous;
        });
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
    }

    function onSelectLocations(locations) {
        setSelectedLocations([...locations]);
    }

    // Opens the infringements modal for an employee. weekNumber filters the modal to a single ISO
    // week; null (the default, used by the period total) shows every week. Both pieces of modal state
    // are set together so switching between a per-week link and the total never leaves a stale filter.
    function openInfringements(employee, weekNumber = null) {
        setInfringementsEmployee(employee);
        setSelectedWeekNumber(weekNumber);
    }

    function closeInfringements() {
        setInfringementsEmployee(null);
        setSelectedWeekNumber(null);
    }

    // Human-readable period range, e.g. "30 March – 26 April 2026".
    let periodRangeText = "";
    if (period != null) {
        periodRangeText = `${moment(period.startDate, apiDateFormat).format("DD MMMM")} – ${moment(period.endDate, apiDateFormat).format("DD MMMM YYYY")}`;
    }

    // Headers for the per-interval count columns: the assessed periods, or the ISO weeks every other
    // driver KPI shows. D8 has neither.
    let infringementColumnHeaders = [];
    if (usePeriodColumns) {
        // The shared period-header tooltip, so the same period cannot be described two different ways
        // across this page and the KPI summary grid.
        infringementColumnHeaders = periodColumns.map((periodColumn) => {
            return (
                <Tooltip key={`period-${periodColumn.isoYear}-${periodColumn.isoPeriod}`} content={formatPeriodHeaderTooltip(periodColumn.isoYear, periodColumn.isoPeriod)}>
                    <span>{`Period ${periodColumn.isoPeriod}`}</span>
                </Tooltip>
            );
        });
    } else if (!isUnaccountedKm) {
        if (employees.length > 0 && employees[0].weeks != null) {
            infringementColumnHeaders = employees[0].weeks.map((week) => `Week ${week.weekNumber}`);
        } else {
            for (let index = 0; index < isoWeeksPerPeriod; index++) {
                infringementColumnHeaders.push(`Week ${index + 1}`);
            }
        }
    }

    // Tints the selected period's column, header and body cells alike. The two leading empty entries
    // skip the Employee Name and Location columns the period columns sit after.
    let listingColumnClasses = [];
    if (usePeriodColumns) {
        listingColumnClasses = ["", ""];
        periodColumns.forEach((periodColumn) => {
            listingColumnClasses.push(periodColumn.isSelectedPeriod === true ? selectedPeriodColumnClass : "");
        });
    }

    // The listing is built from a different table per KPI: D8 lists vehicles, every driver KPI lists
    // drivers.
    let headers;
    let rows;
    if (isUnaccountedKm) {
        headers = [
            registrationHeader,
            "FLEET NUMBER",
            "LOCATION",
            "UNACCOUNTED KM"
        ];
        rows = vehicles.map((vehicle) => {
            // The vehicle status icon shares the Registration column, coloured by asset status with
            // the ownership icon overlaid — matching the Maintenance drilldown's registration cell.
            // The D8 rows carry flat nullable ids (assetStatusId / assetOwnershipId); when either is
            // null or absent, AssetStatus falls back to an uncoloured block with no icon or tooltip.
            const registrationCell = (
                <div className="nowrap inline-items no-spacing">
                    <AssetStatus
                        statusId={vehicle.assetStatusId}
                        ownershipId={vehicle.assetOwnershipId}
                        isLoading={listingLoading}
                        statusAttributeMappings={assetStatusMappings}
                        ownershipAttributeMappings={assetOwnershipMappings}
                        tooltipPosition={Position.RIGHT}
                    />
                    <Link to={LinkService.getVehicleProfileUrl(vehicle.assetId, "details")}>{vehicle.registration}</Link>
                </div>
            );

            return [
                registrationCell,
                vehicle.fleetNumber,
                vehicle.locationName,
                vehicle.unaccountedKm
            ];
        });
    } else {
        headers = [
            employeeNameHeader,
            "LOCATION",
            ...infringementColumnHeaders,
            renderedTotalInfringementsHeader,
            renderedDriverDaysHeader,
            "ACTIONS"
        ];
        rows = employees.map((employee) => {
            // Total Infringements is the server-computed sum of the selected KPI's interval cells, used
            // both for the column value and to decide whether the row opens the infringements modal.
            const totalInfringements = employee.totalInfringements;
            // With the period columns in use the driver days span the three assessed periods, so the
            // server returns the total; the week columns have no server-level total and are summed here.
            const totalDriverDays = usePeriodColumns ? employee.driverDays : sumWeeks(employee.weeks, "driverDays");

            // The status circle and the employee name share the Employee Name column. The circle is
            // coloured by driver status (away → orange, inactive → pink, otherwise teal), with the status
            // as its tooltip; matches the CircleStatus used on the Collisions listing.
            const nameCell = (
                <div className="inline-items">
                    <div className="inline-item">
                        <CircleStatus
                            status={employee.driverStatus?.name}
                            hollow={employee.driverType?.name?.toLowerCase() === "agency"}
                            tooltip={`${employee.driverStatus?.name} ${employee.driverType?.name?.toLowerCase()} driver`}
                        />
                    </div>
                    <div className="inline-item">
                        <a href={LinkService.getDriverProfileUrl(employee.employeeId)}>{employee.employeeName}</a>
                    </div>
                </div>
            );

            // Period counts are plain text because the infringements modal filters by ISO week and so
            // cannot represent a whole period. Either way a missing entry reads "-", matching the
            // summary page; a non-zero week count links to that week's modal.
            let infringementCells;
            if (usePeriodColumns) {
                // The cells walk the header columns and look each period up on the row, so a row can
                // never contribute a different number of cells than there are headers above them.
                infringementCells = periodColumns.map((periodColumn) => {
                    const employeePeriod = (employee.periods || []).find((candidate) =>
                        candidate.isoYear === periodColumn.isoYear && candidate.isoPeriod === periodColumn.isoPeriod);
                    let periodContent = "-";
                    if (employeePeriod != null) {
                        periodContent = employeePeriod.infringementCount;
                    }

                    return (
                        <span key={`period-${employee.employeeId}-${periodColumn.isoYear}-${periodColumn.isoPeriod}`}>{periodContent}</span>
                    );
                });
            } else {
                infringementCells = (employee.weeks || []).map((week) => {
                    let weekContent;
                    if (week.infringementCount == null) {
                        weekContent = "-";
                    } else if (week.infringementCount > 0) {
                        weekContent = (
                            <TextButton onClick={() => openInfringements(employee, week.weekNumber)}>
                                {week.infringementCount}
                            </TextButton>
                        );
                    } else {
                        weekContent = week.infringementCount;
                    }

                    return (
                        <span key={`week-${employee.employeeId}-${week.weekNumber}`}>{weekContent}</span>
                    );
                });
            }

            // A non-zero week total opens the infringements modal; zero is plain text. A three-period
            // total stays text, as the week-filtered modal cannot represent it.
            let totalInfringementsCell;
            if (!usePeriodColumns && totalInfringements > 0) {
                totalInfringementsCell = (
                    <TextButton onClick={() => openInfringements(employee)}>
                        {totalInfringements}
                    </TextButton>
                );
            } else {
                totalInfringementsCell = (
                    <span>{totalInfringements}</span>
                );
            }

            // Deep link into the legacy Vision driver calendar, framed to show the whole ISO period
            // (see calendarWeekThreeOffsetDays).
            const calendarActivityDate = moment(startDate, apiDateFormat).add(calendarWeekThreeOffsetDays, "days").format(apiDateFormat);
            const calendarUrl = LinkService.getDriverCalendarUrl(employee.employeeId, calendarActivityDate);
            const actionsCell = (
                <div className="button-row-small">
                    <Tooltip content="Driver Calendar" position="top">
                        <LinkButton
                            href={calendarUrl}
                            externalLink
                            minimal
                            iconOnly
                            large={false}
                            icon="calendar"
                        />
                    </Tooltip>
                </div>
            );

            return [
                nameCell,
                employee.locationName,
                ...infringementCells,
                totalInfringementsCell,
                totalDriverDays,
                actionsCell
            ];
        });
    }

    // Flatten the open employee's infringements for the detail modal. When a single week is selected
    // (a per-week link), only that week's infringements are shown; otherwise all weeks (the total).
    const infringementRows = infringementsEmployee != null
        ? (infringementsEmployee.weeks || [])
            .filter((week) => selectedWeekNumber == null || week.weekNumber === selectedWeekNumber)
            .flatMap((week) => week.infringements || [])
            .map((infringement) => {
                // occurredAt is a UTC timestamp shown in UK local time; DateToLocal renders blank for
                // null/invalid values via invalidDateString.
                return [
                    <DateToLocal key={`${infringement.code}-${infringement.description}-${infringement.occurredAt}`} format={infringementDateFormat} invalidDateString="">{infringement.occurredAt}</DateToLocal>,
                    infringement.code,
                    infringement.description
                ];
            })
        : [];

    return (
        <React.Fragment>
            <Breadcrumbs items={breadcrumbs} />
            <h1>{headingText}</h1>
            <div className="inline-items er-drilldown-kpi-row">
                <ShowHide
                    evaluator={hasError}
                    show={(<p>Unable to load KPI options. Please try again later.</p>)}
                    hide={(
                        <FormSelect
                            items={selectItems}
                            selectedValue={kpi}
                            loading={loading}
                            placeholder="Choose a KPI"
                            onItemSelect={handleSelectKpi}
                        />
                    )}
                />
                <div className="er-drilldown-period-stepper">
                    <Tooltip content="Previous period">
                        <Button minimal iconOnly icon="fast-backward" disabled={isAtOrBeforeFloor} onClick={onPreviousPeriod} />
                    </Tooltip>
                    <span className="er-drilldown-period-label">{`Period ${isoPeriod} – ${isoYear}`}</span>
                    <Tooltip content="Next period">
                        <Button minimal iconOnly icon="fast-forward" disabled={isCurrentPeriod} onClick={onNextPeriod} />
                    </Tooltip>
                    <Tooltip content="Current period">
                        <Button minimal iconOnly icon="reset" disabled={isCurrentPeriod} onClick={onResetPeriod} />
                    </Tooltip>
                </div>
            </div>
            <ShowHide
                evaluator={periodHasError}
                show={(<p>Unable to load the period summary. Please try again later.</p>)}
                hide={(
                    <React.Fragment>
                        <div className="er-drilldown-summary">
                            <div className="er-drilldown-summary-period">
                                <span className={classNames("er-drilldown-summary-label", { "bp6-skeleton": periodLoading })}>Period</span>
                                <span className={classNames("er-drilldown-summary-value", { "bp6-skeleton": periodLoading })}>{periodRangeText}</span>
                            </div>
                            <ShowHide
                                evaluator={!isUnaccountedKm}
                                show={(
                                    <div className="er-drilldown-summary-item">
                                        <span className={classNames("er-drilldown-summary-label", { "bp6-skeleton": periodLoading })}>Period Status</span>
                                        <div className="er-drilldown-summary-status">
                                            <ShowHide
                                                evaluator={!isCountKpi}
                                                show={(
                                                    <KeyBlock colours={resolvePeriodStatusColours()} toolTipText={resolvePeriodStatusTooltip()} loading={periodLoading} />
                                                )}
                                            />
                                            <span className={classNames("er-drilldown-summary-value", { "bp6-skeleton": periodLoading })}>{resolvePeriodStatusValue()}</span>
                                        </div>
                                    </div>
                                )}
                            />
                            <ShowHide
                                evaluator={isUnaccountedKm}
                                show={(
                                    <div className="er-drilldown-summary-item">
                                        <span className={classNames("er-drilldown-summary-label", { "bp6-skeleton": periodLoading })}>Total Unaccounted KM</span>
                                        <span className={classNames("er-drilldown-summary-value", { "bp6-skeleton": periodLoading })}>{period != null ? period.totalUnaccountedKm : ""}</span>
                                    </div>
                                )}
                                hide={(
                                    <React.Fragment>
                                        <div className="er-drilldown-summary-item">
                                            <span className={classNames("er-drilldown-summary-label", { "bp6-skeleton": periodLoading })}>Total Infringements</span>
                                            <span className={classNames("er-drilldown-summary-value", { "bp6-skeleton": periodLoading })}>{period != null ? period.totalInfringements : ""}</span>
                                        </div>
                                        <div className="er-drilldown-summary-item">
                                            <span className={classNames("er-drilldown-summary-label", { "bp6-skeleton": periodLoading })}>Total Driver Days</span>
                                            <span className={classNames("er-drilldown-summary-value", { "bp6-skeleton": periodLoading })}>{period != null ? period.driverDays : ""}</span>
                                        </div>
                                    </React.Fragment>
                                )}
                            />
                        </div>
                    </React.Fragment>
                )}
            />
            {/* The filter controls (search + location selector + filters) and the listing render
                regardless of periodHasError, so a failed period-summary load never hides the location
                selector the user needs to narrow the selection and recover (Bug 169528). */}
            <div className="spacer-bottom"></div>
            <div className="filter-controls">
                <div className="inline-items">
                    <div className="inline-item fill">
                        <div className="form-field-full">
                            <FormTextInput
                                id="er-drilldown-employee-search"
                                placeholder={isUnaccountedKm ? "Registration or Fleet Number" : "Employee Name"}
                                value={searchTerm}
                                onChange={onSearchChange}
                                disabled={listingLoading}
                                large
                                icon="search"
                            />
                        </div>
                    </div>
                    <div className="inline-item">
                        <FormLocationSelector
                            businessArea=""
                            loading={customLocations == null}
                            disabled={listingLoading}
                            selectedLocations={selectedLocations}
                            setSelectedLocations={onSelectLocations}
                            useHierarchy={useHierarchy}
                            setUseHierarchy={setUseHierarchy}
                            useCustomLocations
                            customLocations={customLocations}
                            useLocationDefaults={false}
                            locationDefaultsToTrue
                        />
                    </div>
                    <ShowHide
                        evaluator={!isUnaccountedKm}
                        show={(
                            <div className="inline-item">
                                <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                            </div>
                        )}
                    />
                </div>
                <ShowHide
                    evaluator={!isUnaccountedKm}
                    show={(
                        <Filter
                            filterName="driverHoursDrilldown"
                            visible={showFilters}
                            filters={pageFilters}
                            onUpdate={onFilterChange}
                            onClickOutside={onToggleFilter}
                        />
                    )}
                />
            </div>
            <div className="spacer-bottom"></div>
            <ListingTable
                id="er-drilldown-listing"
                headers={headers}
                data={rows}
                totalRecordCount={totalRecords}
                onPagingChange={onPagingChange}
                loadingData={listingLoading}
                noDataMessage={isUnaccountedKm ? "No vehicles were found for your search criteria" : "No drivers were found for your search criteria"}
                columnClasses={listingColumnClasses}
                sortable
                sortableHeaders={sortableHeaders}
                sortedBy={activeSortColumn.header}
                sortedDir={tableSortDir}
                onSort={onTableSort}
            />
            <Modal
                title="Infringements"
                isOpen={infringementsEmployee != null}
                onClose={closeInfringements}
                size="large"
            >
                <ListingTable
                    id="er-drilldown-infringements-listing"
                    loadingData={false}
                    data={infringementRows}
                    headers={["DATE", "CODE", "DESCRIPTION"]}
                    totalRecordCount={infringementRows.length}
                    pageable={false}
                    sortable={false}
                />
            </Modal>
        </React.Fragment>
    );
}

DriverKpiDrilldown.propTypes = {
    breadcrumbs: PropTypes.array
};
